import { apiClient } from '@services/api-client'
import { useClients } from '@services/users/queries/useClients'
import { SNA_PREFIX } from '@services/utils'
import { X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { UserList } from 'types/api'
import type { BaseResponse } from 'types/utils'

import { cn } from '@/lib/utils'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True if the email belongs to a user in our DB (exact, case-insensitive). */
async function emailExists(email: string): Promise<boolean> {
  try {
    const res = await apiClient.get<BaseResponse<UserList>>(
      `${SNA_PREFIX}users`,
      { params: { search: email, limit: 5 } }
    )
    const target = email.toLowerCase()
    return (res.data.results ?? []).some(
      (u) => u.email?.toLowerCase() === target
    )
  } catch {
    return false
  }
}
const CATEGORIES = ['USER', 'TENANT', 'LANDLORD'] as const
type Category = (typeof CATEGORIES)[number]

type Option = { email: string; label: string }

function toOptions(
  users: UserList[] | undefined,
  exclude: (email: string) => boolean
): Option[] {
  const out: Option[] = []
  for (const u of users ?? []) {
    if (!u.email || exclude(u.email)) continue
    out.push({
      email: u.email,
      label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email
    })
  }
  return out
}

interface EmailAutocompleteProps {
  value: string[]
  onChange: (emails: string[]) => void
  invalid?: boolean
}

export function EmailAutocomplete({
  value,
  onChange,
  invalid
}: EmailAutocompleteProps) {
  const { t } = useTranslation('admin')
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [inputError, setInputError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const enabled = debounced.length > 0
  const search = enabled ? debounced : ''

  const clients = useClients({ params: { search, limit: 15, roles: 'USER' } })
  const tenants = useClients({
    params: { search, limit: 15, has_ongoing_offer: true }
  })
  const landlords = useClients({
    params: { search, limit: 15, roles: 'LANDLORD' }
  })

  const grouped = useMemo(() => {
    const selected = new Set(value)
    const tenantOptions = toOptions(tenants.data?.results, (e) =>
      selected.has(e)
    )
    const tenantEmails = new Set(tenantOptions.map((o) => o.email))
    return {
      USER: toOptions(
        clients.data?.results,
        (e) => selected.has(e) || tenantEmails.has(e)
      ),
      TENANT: tenantOptions,
      LANDLORD: toOptions(landlords.data?.results, (e) => selected.has(e))
    } as Record<Category, Option[]>
  }, [clients.data, tenants.data, landlords.data, value])

  const hasResults = CATEGORIES.some((c) => grouped[c].length > 0)
  const isFetching =
    clients.isFetching || tenants.isFetching || landlords.isFetching

  const addEmail = (email: string) => {
    if (!value.includes(email)) onChange([...value, email])
  }

  const removeEmail = (email: string) =>
    onChange(value.filter((e) => e !== email))

  // Only emails belonging to an existing user may be added — verify each
  // well-formed address against the DB before turning it into a chip.
  const addTyped = async (raw: string) => {
    const parts = raw
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return

    const badFormat = parts.filter((p) => !EMAIL_RE.test(p))
    const candidates = parts.filter((p) => EMAIL_RE.test(p))

    const checks = await Promise.all(candidates.map(emailExists))
    const known = candidates.filter((c, i) => checks[i] && !value.includes(c))
    const unknown = candidates.filter((_, i) => !checks[i])

    if (known.length) onChange([...value, ...known])
    setQuery([...badFormat, ...unknown].join(' '))
    setInputError(
      badFormat.length
        ? t('sendEmail.validation.emailInvalid')
        : unknown.length
          ? t('sendEmail.validation.emailNotRegistered')
          : null
    )
  }

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => setOpen(true)}
        className={cn(
          'flex min-h-11 flex-wrap items-center gap-1.5 rounded-2xl border bg-white px-3 py-2 transition-colors focus-within:border-primary',
          invalid || inputError ? 'border-destructive' : 'border-ink/[0.16]'
        )}
      >
        {value.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] py-0.5 pl-2.5 pr-1 text-sm text-ink"
          >
            {email}
            <button
              type="button"
              onClick={() => removeEmail(email)}
              className="flex h-4 w-4 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink/50 hover:text-ink"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          placeholder={t('sendEmail.recipients.addPlaceholder')}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setInputError(null)
            setOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
              e.preventDefault()
              addTyped(query)
            } else if (e.key === 'Backspace' && !query && value.length) {
              removeEmail(value[value.length - 1]!)
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          onPaste={(e) => {
            e.preventDefault()
            addTyped(e.clipboardData.getData('text'))
          }}
          className="h-7 min-w-[14rem] flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-muted-foreground"
        />
      </div>

      {inputError && (
        <p className="mt-1.5 text-sm font-medium text-destructive">
          {inputError}
        </p>
      )}

      {open && enabled && (
        <div className="absolute z-[1200] mt-1.5 max-h-72 w-full overflow-y-auto rounded-2xl border border-ink/[0.08] bg-white p-1 shadow-[0_16px_40px_rgba(4,33,37,0.16)]">
          {isFetching && !hasResults ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">…</div>
          ) : !hasResults ? (
            <div className="px-3 py-3 text-sm text-muted-foreground">
              {t('sendEmail.recipients.noResults')}
            </div>
          ) : (
            CATEGORIES.map((cat) =>
              grouped[cat].length > 0 ? (
                <div key={cat} className="mb-1 last:mb-0">
                  <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-ink/40">
                    {t(`sendEmail.recipients.categories.${cat}`)}
                  </div>
                  {grouped[cat].map((o) => (
                    <button
                      key={o.email}
                      type="button"
                      onClick={() => {
                        addEmail(o.email)
                        setQuery('')
                        setInputError(null)
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-ink/[0.04]"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium text-ink">
                        {o.label}
                      </span>
                      <span className="shrink-0 truncate text-xs text-muted-foreground">
                        {o.email}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null
            )
          )}
        </div>
      )}
    </div>
  )
}
